/**
 * hebrewCalendar.ts — Hebrew calendar helpers for the synagogue system.
 * Israel schedule only (il: true). Built on @hebcal/core v6 (ESM).
 *
 * Shared by the web app and Cloud Functions.
 * On the server, set process.env.TZ = 'Asia/Jerusalem' before any Date use
 * (first line of functions/src/index.ts). All helpers work in LOCAL time.
 *
 * Date keys are 'YYYY-MM-DD' strings (local). Never use toISOString() for
 * date keys — it shifts to UTC and returns the previous day in Israel.
 */
import { HDate, HebrewCalendar, Locale, Location, isAssurBemlacha, months, flags, type Event } from '@hebcal/core';

const LOCALE = 'he-x-NoNikud';

// ---------- date keys ----------

export function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function fromDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function asHDate(x: Date | HDate | string): HDate {
  if (x instanceof HDate) return x;
  if (typeof x === 'string') return new HDate(fromDateKey(x));
  return new HDate(x);
}

// ---------- formatting ----------

/** "כ״ט תשרי תשפ״ז" (no nikud) */
export function hebrewDateString(x: Date | HDate | string): string {
  return asHDate(x).renderGematriya(true);
}

/** "כ״ט תשרי" — without year, for yahrzeit lists */
export function hebrewDayMonth(x: Date | HDate | string): string {
  const full = hebrewDateString(x);
  return full.split(' ').slice(0, -1).join(' ');
}

export function hebrewMonthName(month: number, hyear: number): string {
  return Locale.gettext(HDate.getMonthName(month, hyear), LOCALE);
}

// ---------- Shabbat / parasha ----------

export function isShabbat(d: Date): boolean {
  return d.getDay() === 6;
}

export function shabbatOnOrAfter(d: Date): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() + ((6 - r.getDay() + 7) % 7));
  return r;
}

export function shabbatOnOrBefore(d: Date): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() - ((r.getDay() + 1) % 7));
  return r;
}

export function shabbatStrictlyBefore(d: Date): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
  return shabbatOnOrBefore(r);
}

/**
 * Hebrew label for the Torah reading of a Shabbat in Israel:
 * "בראשית", "תזריע-מצורע", or the holiday name when Shabbat is a chag
 * (e.g. "שמיני עצרת").
 */
export function parashaLabel(d: Date): string {
  const hd = new HDate(d);
  const sedra = HebrewCalendar.getSedra(hd.getFullYear(), true);
  const r = sedra.lookup(hd);
  return r.parsha.map((p: string) => Locale.gettext(p, LOCALE)).join('-');
}

/** Holidays / special Shabbatot on a given date (Israel), Hebrew, no nikud. */
export function holidaysOn(d: Date): string[] {
  const evs = HebrewCalendar.calendar({
    start: d, end: d, il: true, sedrot: false, noMinorFast: false,
  }) as Event[];
  return evs
    .filter((e) => !(e.getFlags() & flags.PARSHA_HASHAVUA))
    .map((e) => e.render(LOCALE).replace(/\s*\d{4}$/, '')); // "ראש השנה 5787" → "ראש השנה"
}

/** True when the date is a Yom Tov in Israel (Torah reading day with its own aliyot layout). */
export function isYomTov(d: Date): boolean {
  const evs = HebrewCalendar.calendar({ start: d, end: d, il: true }) as Event[];
  return evs.some((e) => (e.getFlags() & flags.CHAG) && !(e.getFlags() & flags.EREV));
}

/** 10 Tishrei. */
export function isYomKippur(d: Date): boolean {
  const hd = new HDate(d);
  return hd.getMonth() === months.TISHREI && hd.getDate() === 10;
}

/** 22 Tishrei — Shemini Atzeret, which in Israel is also Simchat Torah. */
export function isSimchatTorahIL(d: Date): boolean {
  const hd = new HDate(d);
  return hd.getMonth() === months.TISHREI && hd.getDate() === 22;
}

/**
 * Aliyot layout families (defaults in aliyotEngine.DEFAULT_SLOT_LAYOUTS, editable in settings):
 * Yom Kippur has its own count (weekday 6 + maftir, Shabbat 7 + maftir) and Simchat Torah
 * has extra aliyot (hosafot), so both get their own kind.
 */
export type ReadingDayKind = 'shabbat' | 'yomtov' | 'shabbatYomTov' | 'yomKippur' | 'shabbatYomKippur' | 'simchatTorah';

export function readingDayKind(d: Date): ReadingDayKind | null {
  const shabbat = isShabbat(d);
  if (isYomKippur(d)) return shabbat ? 'shabbatYomKippur' : 'yomKippur';
  if (isSimchatTorahIL(d)) return 'simchatTorah';
  const yt = isYomTov(d);
  if (shabbat && yt) return 'shabbatYomTov';
  if (shabbat) return 'shabbat';
  if (yt) return 'yomtov';
  return null;
}

export interface ReadingDay {
  dateKey: string;
  kind: ReadingDayKind;
  hebrewDate: string;   // "כ״ט תשרי תשפ״ז"
  label: string;        // "שבת בראשית" / "סוכות א׳" / "שבת חול המועד סוכות"
  parasha: string;      // parasha or holiday reading name
  holidays: string[];
}

/**
 * All Shabbatot and Yamim Tovim (Israel) in [start, end] — these are the
 * "events" on which minyanim record aliyot, sales and chazzan.
 */
export function readingDays(start: Date, end: Date): ReadingDay[] {
  const out: ReadingDay[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  while (cur <= end) {
    const kind = readingDayKind(cur);
    if (kind) {
      const shabbat = isShabbat(cur);
      const yt = isYomTov(cur);
      const hol = holidaysOn(cur);
      const parasha = shabbat ? parashaLabel(cur) : (hol[0] ?? '');
      const base = shabbat && !yt ? parasha : (hol[0] ?? parasha);
      const label = shabbat && !base.startsWith('שבת') ? `שבת ${base}` : base;
      out.push({ dateKey: toDateKey(cur), kind, hebrewDate: hebrewDateString(cur), label, parasha, holidays: hol });
    }
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

// ---------- yahrzeit / anniversaries ----------

/**
 * A Hebrew date as stored on a person card.
 * month uses @hebcal/core numbering: NISAN=1 … ADAR_I=12, ADAR_II=13.
 * In a NON-leap year, month 12 means plain "Adar".
 * year is optional (families often know only day + month).
 */
export interface HebrewDateParts {
  day: number;
  month: number;
  year?: number;
}

/** Custom when the death was in plain Adar (non-leap year) and this year is leap. */
export type AdarRule = 'adar1' | 'adar2';

function isValidParts(p: HebrewDateParts, year: number): boolean {
  if (p.month === months.ADAR_II && !HDate.isLeapYear(year)) return false;
  if (p.month < 1 || p.month > HDate.monthsInYear(year)) return false;
  return p.day >= 1 && p.day <= HDate.daysInMonth(p.month, year);
}

/** Pick a base year in which the stored parts are a real date (for yearless entries). */
function baseYearFor(p: HebrewDateParts): number {
  if (p.year && isValidParts(p, p.year)) return p.year;
  const wantLeap = p.month === months.ADAR_II; // ADAR_I(12) alone = plain Adar → non-leap base
  for (let y = 5780; y > 5700; y--) {
    if (HDate.isLeapYear(y) !== wantLeap) continue;
    if (isValidParts(p, y)) return y;
  }
  throw new Error(`Invalid Hebrew date ${p.day}/${p.month}`);
}

/**
 * Gregorian date of the yahrzeit in Hebrew year `hyear`.
 * Handles 30 Cheshvan/Kislev and Adar rules via hebcal's getYahrzeit,
 * then applies the congregation's AdarRule for plain-Adar deaths in a leap year.
 */
export function yahrzeitDate(p: HebrewDateParts, hyear: number, adarRule: AdarRule = 'adar1'): Date | undefined {
  const base = baseYearFor(p);
  const orig = new HDate(p.day, p.month, base);
  const y = HebrewCalendar.getYahrzeit(hyear, orig);
  if (!y) return undefined;
  const origIsPlainAdar = !HDate.isLeapYear(base) && p.month === months.ADAR_I;
  if (origIsPlainAdar && HDate.isLeapYear(hyear) && adarRule === 'adar2') {
    return new HDate(y.getDate(), months.ADAR_II, hyear).greg();
  }
  return y.greg();
}

/** Next occurrence (today or later) of a yahrzeit, from a reference date. */
export function nextYahrzeit(p: HebrewDateParts, from: Date, adarRule: AdarRule = 'adar1'): Date | undefined {
  const fromDay = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const hy = new HDate(fromDay).getFullYear();
  for (const y of [hy, hy + 1]) {
    const d = yahrzeitDate(p, y, adarRule);
    if (d && d >= fromDay) return d;
  }
  return undefined;
}

export type YahrzeitAliyahRule = 'onOrBefore' | 'strictlyBefore';

/** The Shabbat on which the family member is prioritized (maftir) for a yahrzeit. */
export function yahrzeitShabbat(yahrzeit: Date, rule: YahrzeitAliyahRule = 'strictlyBefore'): Date {
  return rule === 'onOrBefore' ? shabbatOnOrBefore(yahrzeit) : shabbatStrictlyBefore(yahrzeit);
}

/**
 * Every Shabbat with maftir priority for a yahrzeit, earliest first. A yahrzeit that falls ON Shabbat (owner 2026-10-04)
 * gets maftir both on the Shabbat before and on the yahrzeit Shabbat itself, whatever the rule.
 */
export function yahrzeitShabbatot(yahrzeit: Date, rule: YahrzeitAliyahRule = 'strictlyBefore'): Date[] {
  if (!isShabbat(yahrzeit)) return [yahrzeitShabbat(yahrzeit, rule)];
  return [shabbatStrictlyBefore(yahrzeit), new Date(yahrzeit.getFullYear(), yahrzeit.getMonth(), yahrzeit.getDate())];
}

/** Hebrew 13th birthday (bar mitzvah date) for a boy born on the given Hebrew date. */
export function barMitzvahDate(birth: HebrewDateParts & { year: number }): Date {
  const b = new HDate(birth.day, birth.month, birth.year);
  const d = HebrewCalendar.getBirthdayOrAnniversary(birth.year + 13, b);
  if (!d) throw new Error('Could not compute bar mitzvah date');
  return d.greg();
}

/** Hebrew parts from a Gregorian date (use when the user enters a civil date). */
export function toHebrewParts(d: Date): Required<HebrewDateParts> {
  const hd = new HDate(d);
  return { day: hd.getDate(), month: hd.getMonth(), year: hd.getFullYear() };
}

/** Current Hebrew year (for annual reports that run Tishrei–Elul). */
export function currentHebrewYear(d: Date = new Date()): number {
  return new HDate(d).getFullYear();
}

/** Gregorian range of a Hebrew year: 1 Tishrei … 29 Elul. */
export function hebrewYearRange(hyear: number): { start: Date; end: Date } {
  const start = new HDate(1, months.TISHREI, hyear).greg();
  const end = new HDate(1, months.TISHREI, hyear + 1).greg();
  end.setDate(end.getDate() - 1);
  return { start, end };
}

export { months as HebrewMonths };

// ---------- Shabbat / Yom Tov pause (owner request 2026-09-28) ----------

/** Jerusalem times (the synagogue is in the Jerusalem area; Israel schedule — one day of Yom Tov). */
const JERUSALEM = new Location(31.7683, 35.2137, true, 'Asia/Jerusalem', 'Jerusalem', 'IL', undefined, 754);
/** Margins around hebcal's window (sunset → tzeit 8.5°): candle lighting before, a safe nightfall after. */
export const SHABBAT_PAUSE_BEFORE_MIN = 45;
export const SHABBAT_PAUSE_AFTER_MIN = 30;

/**
 * True from 45 minutes before sunset on Erev Shabbat / Erev Yom Tov until 30 minutes after nightfall at the end —
 * the system sends nothing to people then (emails wait in the queue and go out after Shabbat / the holiday).
 */
export function isShabbatPause(at: Date = new Date()): boolean {
  const t = at.getTime();
  return isAssurBemlacha(at, JERUSALEM, false)
    || isAssurBemlacha(new Date(t + SHABBAT_PAUSE_BEFORE_MIN * 60_000), JERUSALEM, false)
    || isAssurBemlacha(new Date(t - SHABBAT_PAUSE_AFTER_MIN * 60_000), JERUSALEM, false);
}
